import {
	getGalleryMedia,
	startMediaDownload,
	estimateMediaDownload,
	getMediaDownloadStatus,
} from '@/app/services/albums/albums';
import * as Actions from '@/app/store/actions';
import { AppBar, CircularProgress, InputAdornment, TextField } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { StyledTab } from '@shared/components/StyledTab';
import StyledTabs from '@shared/components/StyledTabs';
import GradientButton from '@shared/components/GradientButton';
import AppliedFiltersBar from '@shared/Reports/AppliedFiltersBar';
import GalleryAlbums from './GalleryAlbums';
import GalleryFiltersBar from './GalleryFiltersBar';
import Lightbox from './Lightbox';
import usePreloadImages from './usePreloadImages';
import AddToAlbumModal from './AddToAlbumModal';
import CreateAlbumModal from './CreateAlbumModal';
import DownloadProgressModal from '@/app/customComponents/modals/DownloadProgressModal';
import './Gallery.css';

const Gallery = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const activeTab = location.pathname === '/memories/albums' ? 'albums' : 'photos';
	const [media, setMedia] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(null);
	const [totalItems, setTotalItems] = useState(0);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [lightboxIndex, setLightboxIndex] = useState(null);
	const [addToAlbumOpen, setAddToAlbumOpen] = useState(false);
	const [createAlbumOpen, setCreateAlbumOpen] = useState(false);
	const [albumsCreateOpen, setAlbumsCreateOpen] = useState(false);
	const [albumSearchQuery, setAlbumSearchQuery] = useState('');
	const [downloadProgress, setDownloadProgress] = useState(0);
	const [downloadModalOpen, setDownloadModalOpen] = useState(false);
	const [downloadLink, setDownloadLink] = useState(null);

	// Selection state: two modes
	// manual: selectedIds is the set of picked IDs
	// select_all: everything matching current filters is selected, excludedIds tracks unchecked items
	const [selectionMode, setSelectionMode] = useState('manual'); // 'manual' | 'select_all'
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [excludedIds, setExcludedIds] = useState(new Set());

	const user = useSelector((state) => state.auth.user);
	const dispatch = useDispatch();
	const cache = useRef({});

	const [photoFilters, setPhotoFilters] = useState({
		room_id: [],
		student_id: [],
		activity_id: [],
		album_id: [],
		media_type: null,
		start_date: null,
		end_date: null,
	});

	const [albumFilters, setAlbumFilters] = useState({
		room_id: [],
		type: null,
	});

	// Build API filter params from photoFilters state
	const buildFilterParams = useCallback(() => {
		const params = {};
		if (photoFilters.room_id?.length > 0) {
			params.room_id = photoFilters.room_id.map((r) => r.value);
		}
		if (photoFilters.student_id?.length > 0) {
			params.student_id = photoFilters.student_id.map((s) => s.value);
		}
		if (photoFilters.activity_id?.length > 0) {
			params.activity_id = photoFilters.activity_id.map((a) => a.value);
		}
		if (photoFilters.album_id?.length > 0) {
			params.album_id = photoFilters.album_id.map((a) => a.value);
		}
		if (photoFilters.media_type && photoFilters.media_type.value !== 'all') {
			params.type = photoFilters.media_type.value;
		}
		if (photoFilters.start_date) {
			params.start_date = photoFilters.start_date;
		}
		if (photoFilters.end_date) {
			params.end_date = photoFilters.end_date;
		}
		return params;
	}, [photoFilters]);

	const fetchMedia = useCallback(async () => {
		try {
			setLoading(true);
			const params = { page, ...buildFilterParams() };

			const queryKey = JSON.stringify(params);
			if (cache.current[queryKey]) {
				setMedia(cache.current[queryKey].data);
				setTotalPages(cache.current[queryKey].totalPages);
				setTotalItems(cache.current[queryKey].totalItems);
				setHasNextPage(cache.current[queryKey].hasNextPage);
				setLoading(false);
				return;
			}

			const res = await getGalleryMedia(params);
			const mediaItems = res.data.data || [];
			const lastPage = res.data?.meta?.last_page || res.data?.last_page || null;
			const total = res.data?.meta?.total || res.data?.total || 0;
			const nextPage = !!(res.data?.next_page_url || res.data?.links?.next);

			cache.current[queryKey] = {
				data: mediaItems,
				totalPages: lastPage,
				totalItems: total,
				hasNextPage: nextPage,
			};
			setMedia(mediaItems);
			setTotalPages(lastPage);
			setTotalItems(total);
			setHasNextPage(nextPage);
		} catch (error) {
			dispatch(Actions.showMessage({ message: 'Failed to fetch gallery media', variant: 'error' }));
			setMedia([]);
		} finally {
			setLoading(false);
		}
	}, [page, buildFilterParams]);

	useEffect(() => {
		if (activeTab === 'photos') {
			fetchMedia();
		}
	}, [fetchMedia, activeTab]);

	// Reset page and selection when filters change
	useEffect(() => {
		setPage(1);
		cache.current = {};
		clearSelection();
	}, [photoFilters]);

	usePreloadImages(media);

	const clearSelection = () => {
		setSelectionMode('manual');
		setSelectedIds(new Set());
		setExcludedIds(new Set());
	};

	// Build a unique key for a media item to handle both sources (media vs activity).
	const mediaKey = (item) => `${item.media_source || 'media'}:${item.id}`;

	const isItemSelected = (item) => {
		if (selectionMode === 'select_all') {
			return !excludedIds.has(mediaKey(item));
		}
		return selectedIds.has(mediaKey(item));
	};

	const getSelectedCount = () => {
		if (selectionMode === 'select_all') {
			return totalItems - excludedIds.size;
		}
		return selectedIds.size;
	};

	const handleMediaSelect = (item) => {
		const key = mediaKey(item);
		if (selectionMode === 'select_all') {
			setExcludedIds((prev) => {
				const next = new Set(prev);
				if (next.has(key)) {
					next.delete(key);
				} else {
					next.add(key);
				}
				// If everything is excluded, revert to manual mode
				if (next.size >= totalItems) {
					setSelectionMode('manual');
					setSelectedIds(new Set());
					return new Set();
				}
				return next;
			});
		} else {
			setSelectedIds((prev) => {
				const next = new Set(prev);
				if (next.has(key)) {
					next.delete(key);
				} else {
					next.add(key);
				}
				return next;
			});
		}
	};

	const handleSelectAll = () => {
		if (selectionMode === 'select_all' && excludedIds.size === 0) {
			// Already all selected — deselect all
			clearSelection();
		} else {
			// Select all across all pages
			setSelectionMode('select_all');
			setExcludedIds(new Set());
			setSelectedIds(new Set());
		}
	};

	const isAllSelected = selectionMode === 'select_all' && excludedIds.size === 0;

	// Build the payload for select_all or manual mode
	const buildSelectionPayload = () => {
		if (selectionMode === 'select_all') {
			return {
				select_all: true,
				filters: buildFilterParams(),
				excluded_items: Array.from(excludedIds).map((key) => {
					const [source, id] = key.split(':');
					return { id: parseInt(id), source };
				}),
			};
		}
		const items = Array.from(selectedIds).map((key) => {
			const [source, id] = key.split(':');
			return { id: parseInt(id), source };
		});
		return { items };
	};

	const handleAddToAlbumComplete = () => {
		clearSelection();
	};

	const handleCreateAlbumFromSelection = () => {
		setCreateAlbumOpen(true);
	};

	const handleAlbumCreated = () => {
		clearSelection();
		setCreateAlbumOpen(false);
	};

	const handleDownload = async () => {
		const count = getSelectedCount();
		if (count === 0) return;

		const payload = buildSelectionPayload();

		try {
			// Check size estimate first
			const estimateRes = await estimateMediaDownload(payload);
			const { exceeds_limit, total_bytes, max_items } = estimateRes.data;

			if (exceeds_limit) {
				const msg = max_items
					? `You can download up to ${max_items} items at a time. Please narrow your selection or use filters.`
					: `Selection is ~${Math.round(total_bytes / 1024 / 1024)} MB (limit is 1 GB). Please deselect some items or narrow your filters.`;
				dispatch(Actions.showMessage({ message: msg, variant: 'error' }));
				return;
			}

			setDownloadProgress(0);
			setDownloadLink(null);
			setDownloadModalOpen(true);

			const response = await startMediaDownload(payload);
			const jobId = response.data.job_id;

			pollDownloadProgress(jobId);
		} catch (error) {
			const msg = error.response?.data?.message || 'Failed to start download.';
			dispatch(Actions.showMessage({ message: msg, variant: 'error' }));
			setDownloadModalOpen(false);
		}
	};

	const pollDownloadProgress = async (jobId) => {
		try {
			const res = await getMediaDownloadStatus(jobId);
			const { status, progress, download_url, message } = res.data;

			setDownloadProgress(progress);

			if (status === 'completed' && progress === 100) {
				const link = document.createElement('a');
				link.href = download_url;
				link.setAttribute('download', '');
				link.style.display = 'none';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				setDownloadProgress(null);
				setDownloadLink(download_url);
			} else if (status === 'failed') {
				dispatch(Actions.showMessage({ message: message || 'Download failed.', variant: 'error' }));
				setDownloadProgress(null);
				setDownloadModalOpen(false);
			} else {
				setTimeout(() => pollDownloadProgress(jobId), 3000);
			}
		} catch {
			setDownloadProgress(null);
			setDownloadModalOpen(false);
		}
	};

	const updatePhotoFilters = (newFilters) => {
		setPhotoFilters(newFilters);
	};

	const updateAlbumFilters = (newFilters) => {
		setAlbumFilters(newFilters);
	};

	const handleTabChange = (tab) => {
		navigate(tab === 'albums' ? '/memories/albums' : '/memories');
		clearSelection();
	};

	const selectedCount = getSelectedCount();

	return (
		<div className="gallery-container">
			{/* Tabs */}
			<AppBar className="relative" position="relative">
				<StyledTabs
					onChange={(_, value) => handleTabChange(value)}
					value={activeTab}
					variant="scrollable"
				>
					<StyledTab label="All Photos/Videos" value="photos" />
					<StyledTab label="Albums" value="albums" />
				</StyledTabs>
				<div className="absolute right-32 flex items-center gap-16 top-1/2 -translate-y-1/2">
					{activeTab === 'albums' && (
						<GradientButton
							onClick={() => setAlbumsCreateOpen(true)}
							size="appbar"
							type="button"
							variant="light-blue"
						>
							<PlusCircleIcon className="w-24" />
							New Album
						</GradientButton>
					)}
				</div>
			</AppBar>

			{/* Header */}
			<div className="gallery-header">
				<h1 className="font-bold text-16 md:text-20 xl:text-24 my-0">{user?.data?.school?.name || 'School'} Memories</h1>
				<div className="flex items-center gap-10 justify-end">
					{activeTab === 'photos' && media.length > 0 && (
						<button
							className="flex items-center gap-8 border-2 border-[#028AFD] rounded-full px-16 py-8 text-14 font-bold text-[#028AFD] hover:bg-blue-50 cursor-pointer bg-white transition-colors"
							onClick={handleSelectAll}
							type="button"
						>
							{isAllSelected ? (
								<>
									<svg className="w-16 h-16" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" /></svg>
									Deselect All
								</>
							) : (
								<>
									<svg className="w-16 h-16" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
									Select All
								</>
							)}
						</button>
					)}
					{activeTab === 'albums' && (
						<div>
							<TextField
								InputProps={{
									endAdornment: (
										<InputAdornment disablePointerEvents position="end">
											<img alt="search-icon" className="w-16" src="assets/images/search-icon.svg" />
										</InputAdornment>
									),
								}}
								className="border-none"
								label="Search albums"
								style={{ width: 225 }}
								margin="none"
								onChange={(e) => setAlbumSearchQuery(e.target.value)}
								size="small"
								value={albumSearchQuery}
							/>
						</div>
					)}
				</div>
			</div>

			{/* Filters — album filters hidden until room/type filtering is needed */}
			{activeTab === 'photos' && (
				<>
					<GalleryFiltersBar filters={photoFilters} setFilters={updatePhotoFilters} tab="photos" />
					<AppliedFiltersBar
						filters={photoFilters}
						hideFilters={['timezone', 'sort', 'dir', 'page']}
						setFilters={updatePhotoFilters}
					/>
				</>
			)}

			{/* Content */}
			{activeTab === 'photos' ? (
				<div>
					{/* Photo Grid */}
					{loading ? (
						<div className="flex justify-center py-64">
							<CircularProgress size={40} />
						</div>
					) : media.length === 0 ? (
						<div className="text-center py-64">
							<p className="text-gray-500 text-18 mb-8">No photos or videos found</p>
							<p className="text-gray-400 text-14">
								Photos and videos from activities will appear here.
							</p>
						</div>
					) : (
						<>
							<div className="gallery-photo-grid">
								{media.map((item, index) => (
									<div
										className={`gallery-photo-item ${isItemSelected(item) ? 'selected' : ''}`}
										key={mediaKey(item)}
									>
										<img
											alt=""
											loading="lazy"
											onClick={() => setLightboxIndex(index)}
											src={item.thumb || item.link}
										/>
										<div className="photo-checkbox">
											<input
												checked={isItemSelected(item)}
												onChange={() => handleMediaSelect(item)}
												onClick={(e) => e.stopPropagation()}
												type="checkbox"
											/>
										</div>
										{item.type === 'video' && (
											<div className="video-duration">Video</div>
										)}
									</div>
								))}
							</div>

							{/* Pagination */}
							{totalPages > 1 ? (
								<div className="gallery-pagination">
									<Pagination
										boundaryCount={1}
										color="primary"
										count={totalPages}
										onChange={(_, value) => setPage(value)}
										page={page}
										shape="rounded"
										siblingCount={0}
									/>
								</div>
							) : !totalPages && (page > 1 || hasNextPage) ? (
								<div className="gallery-pagination">
									<button
										className="pagination-btn"
										disabled={page <= 1}
										onClick={() => setPage(page - 1)}
										type="button"
									>
										Previous
									</button>
									<span className="pagination-page">Page {page}</span>
									<button
										className="pagination-btn"
										disabled={!hasNextPage}
										onClick={() => setPage(page + 1)}
										type="button"
									>
										Next
									</button>
								</div>
							) : null}
						</>
					)}
				</div>
			) : (
				<GalleryAlbums
					createModalOpen={albumsCreateOpen}
					filters={albumFilters}
					searchQuery={albumSearchQuery}
					setCreateModalOpen={setAlbumsCreateOpen}
				/>
			)}

			{/* Selection Bar */}
			{selectedCount > 0 && (
				<div className="gallery-selection-bar">
					<button onClick={clearSelection} type="button">
						&#x2715;
					</button>
					<span className="selection-count">{selectedCount}</span>
					<span className="selection-label">Items Selected</span>
					<button onClick={() => setAddToAlbumOpen(true)} type="button">
						Add to Album
					</button>
					<button onClick={handleDownload} type="button">
						Download
					</button>
				</div>
			)}

			{/* Lightbox */}
			{lightboxIndex !== null && media.length > 0 && (
				<Lightbox
					initialIndex={lightboxIndex}
					media={media}
					onClose={() => setLightboxIndex(null)}
				/>
			)}

			{/* Add to Album Modal */}
			<AddToAlbumModal
				onComplete={handleAddToAlbumComplete}
				onCreateNew={handleCreateAlbumFromSelection}
				open={addToAlbumOpen}
				selectionPayload={buildSelectionPayload()}
				selectedCount={selectedCount}
				setOpen={setAddToAlbumOpen}
			/>

			{/* Create Album Modal (from selection) */}
			<CreateAlbumModal
				onAlbumCreated={handleAlbumCreated}
				open={createAlbumOpen}
				selectionPayload={buildSelectionPayload()}
				selectedCount={selectedCount}
				setOpen={setCreateAlbumOpen}
			/>

			{/* Download Progress Modal */}
			<DownloadProgressModal
				downloadLink={downloadLink}
				open={downloadModalOpen}
				progress={downloadProgress}
				setDownloadLink={setDownloadLink}
				setOpen={setDownloadModalOpen}
			/>
		</div>
	);
};

export default Gallery;
